/**
 * SGK Apps Training Manual PDF generator.
 * Branded, role-wise + menu-wise walkthrough with FAQ.
 * Designed for company admins to onboard, set roles, and train staff.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Inline sanitizer (jsPDF helvetica is Latin-1)
function s(text) {
  if (text == null) return '';
  return String(text)
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E]/g, '"')
    .replace(/[\u2014\u2013\u2010\u2011\u2012]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\u2192\u27A4]/g, '->')
    .replace(/\u2022/g, '*')
    .replace(/\u2122/g, '(TM)')
    .replace(/[\u00B7\u2027]/g, '-')
    .replace(/[^\x00-\x7E\u00A0-\u00FF]/g, '?');
}

const BRAND = {
  // Orange from SGK Apps logo
  primary: [240, 110, 35],
  primaryDark: [200, 80, 25],
  navy: [20, 30, 60],          // SGK navy text
  dark: [15, 23, 42],
  body: [51, 65, 85],
  muted: [100, 116, 139],
  border: [226, 232, 240],
  bandBg: [255, 237, 220],     // light orange
};

// Cover page
function drawCover(doc, opts) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  // Full-page orange band on top half
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, W, H * 0.45, 'F');
  // Logo (centered)
  try {
    doc.addImage(opts.logoDataUrl, 'JPEG', W / 2 - 50, 60, 100, 100);
  } catch { /* ignore */ }
  // Title
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.text(s('SGK Apps (TM)'), W / 2, 200, { align: 'center' });
  doc.setFontSize(16);
  doc.text(s('FM Flexi Pro Training Manual'), W / 2, 224, { align: 'center' });
  // Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(255, 240, 220);
  doc.text(s('Complete guide for company admins, users, and trainers'), W / 2, 246, { align: 'center' });

  // Lower-half meta panel
  doc.setTextColor(...BRAND.navy);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(s('Prepared for'), W / 2, H * 0.55, { align: 'center' });
  doc.setFontSize(14);
  doc.text(s(opts.companyName || 'Your Company'), W / 2, H * 0.55 + 18, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(s(`Generated ${new Date().toLocaleDateString()}`), W / 2, H * 0.55 + 36, { align: 'center' });
  doc.text(s('Branding and a Product of VRV Technical & Technology Solutions FZ LLC'),
    W / 2, H - 60, { align: 'center' });
}

// Page header (used on content pages)
function drawHeader(doc, opts) {
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, W, 50, 'F');
  try {
    doc.addImage(opts.logoDataUrl, 'JPEG', 24, 8, 34, 34);
  } catch { /* ignore */ }
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.text(s('SGK Apps (TM) - FM Flexi Pro Training Manual'), 70, 30);
}

function drawFooter(doc) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let i = 2; i <= total; i++) {  // skip cover
    doc.setPage(i);
    doc.setDrawColor(...BRAND.border);
    doc.setLineWidth(0.5);
    doc.line(32, H - 38, W - 32, H - 38);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text(s('SGK Apps (TM) - Training Manual'), 32, H - 24);
    doc.text(s('VRV Technical & Technology Solutions FZ LLC'), W / 2, H - 24, { align: 'center' });
    doc.text(s(`Page ${i - 1} / ${total - 1}`), W - 32, H - 24, { align: 'right' });
  }
}

// Section heading
function h1(doc, text, y) {
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.primary);
  doc.setFontSize(16);
  doc.text(s(text), 32, y);
  doc.setDrawColor(...BRAND.primary);
  doc.setLineWidth(2);
  doc.line(32, y + 4, 100, y + 4);
  return y + 22;
}

function h2(doc, text, y) {
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.navy);
  doc.setFontSize(12);
  doc.text(s(text), 32, y);
  return y + 16;
}

function paragraph(doc, text, y, opts = {}) {
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.body);
  doc.setFontSize(opts.size || 10);
  const W = doc.internal.pageSize.getWidth();
  const lines = doc.splitTextToSize(s(text), W - 64);
  lines.forEach((line) => {
    if (y > doc.internal.pageSize.getHeight() - 60) {
      doc.addPage();
      drawHeader(doc, opts);
      y = 70;
    }
    doc.text(line, 32, y);
    y += 13;
  });
  return y + 4;
}

function bullets(doc, items, y, headerOpts) {
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.body);
  doc.setFontSize(10);
  const W = doc.internal.pageSize.getWidth();
  items.forEach((item) => {
    const lines = doc.splitTextToSize(s(item), W - 80);
    lines.forEach((line, idx) => {
      if (y > doc.internal.pageSize.getHeight() - 60) {
        doc.addPage();
        drawHeader(doc, headerOpts);
        y = 70;
      }
      doc.text(idx === 0 ? '* ' + line : '  ' + line, 32, y);
      y += 13;
    });
  });
  return y + 4;
}

function ensureSpace(doc, y, needed, headerOpts) {
  if (y + needed > doc.internal.pageSize.getHeight() - 60) {
    doc.addPage();
    drawHeader(doc, headerOpts);
    return 70;
  }
  return y;
}

// === Illustrated Complaint Workflow (swimlane) ===
function drawWorkflowDiagram(doc, headerOpts) {
  // Full page for the diagram
  doc.addPage();
  drawHeader(doc, headerOpts);

  const W = doc.internal.pageSize.getWidth();  // 595
  const left = 30;
  const usable = W - 60;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.navy);
  doc.setFontSize(15);
  doc.text(s('Complaint Lifecycle Diagram'), left, 78);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(s('Who does what, and how the status moves. Branch A: no material. Branch B: material required.'), left, 92);

  // 5 lanes (Tenant, Engineer, Technician, Storekeeper, Status)
  const lanes = [
    { key: 'tenant',      label: 'TENANT',            color: [124, 58, 237],  bg: [245, 243, 255] },
    { key: 'engineer',    label: 'ENGINEER/ADMIN',    color: [217, 119, 6],   bg: [255, 251, 235] },
    { key: 'technician',  label: 'TECHNICIAN',        color: [13, 148, 136],  bg: [240, 253, 250] },
    { key: 'storekeeper', label: 'STOREKEEPER',       color: [37, 99, 235],   bg: [239, 246, 255] },
    { key: 'status',      label: 'STATUS',            color: [51, 65, 85],    bg: [241, 245, 249] },
  ];
  const laneW = usable / lanes.length;
  const laneTop = 105;
  const laneH = 590;
  const headerH = 18;

  // Draw lane backgrounds + headers
  lanes.forEach((l, i) => {
    const x = left + i * laneW;
    doc.setFillColor(...l.bg);
    doc.roundedRect(x + 2, laneTop, laneW - 4, laneH, 6, 6, 'F');
    doc.setFillColor(...l.color);
    doc.roundedRect(x + 2, laneTop, laneW - 4, headerH, 6, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text(s(l.label), x + laneW / 2, laneTop + 12, { align: 'center' });
  });

  // Steps: {lane, title, note, status, statusColor, branch}
  const steps = [
    { lane: 'tenant',      title: '1. Raises complaint',        note: 'Picks building/unit',              status: 'OPEN',                statusColor: [245, 158, 11] },
    { lane: 'engineer',    title: '2. Assigns technician',      note: 'Creates Work Order',               status: 'ASSIGNED',            statusColor: [59, 130, 246] },
    { lane: 'technician',  title: '3. Visits & attends',        note: 'Diagnoses on site',                status: 'IN_PROGRESS',         statusColor: [139, 92, 246] },
    { lane: 'technician',  title: '4A. Completes (no material)', note: 'Photos + tenant signs',           status: 'RESOLVED (A)',        statusColor: [16, 185, 129], branch: 'A' },
    { lane: 'technician',  title: '4B. Requests material',      note: 'Qty/Brand/Size/Returnable',        status: 'MATERIALS_PENDING',   statusColor: [249, 115, 22], branch: 'B' },
    { lane: 'engineer',    title: '5B. Approves material',      note: 'Sets Returnable flag',             status: 'MATERIALS_APPROVED',  statusColor: [34, 197, 94],  branch: 'B' },
    { lane: 'storekeeper', title: '6B. Issues from store',      note: 'Stock auto-decrements',            status: 'MATERIALS_ISSUED',    statusColor: [2, 132, 199],  branch: 'B' },
    { lane: 'technician',  title: '7B. Completes work',         note: 'Returns spares to store',          status: 'RESOLVED (B)',        statusColor: [16, 185, 129], branch: 'B' },
    { lane: 'tenant',      title: '8. Tenant signs off',        note: 'In-app signature',                 status: 'RESOLVED',            statusColor: [16, 185, 129] },
    { lane: 'engineer',    title: '9. Engineer closes',         note: 'Only Engineer/Mgr can close',      status: 'CLOSED',              statusColor: [51, 65, 85] },
  ];

  const laneIndex = (key) => lanes.findIndex(l => l.key === key);
  const cardW = laneW - 12;
  const cardH = 44;
  const gap = 12;
  const stepTop = laneTop + headerH + 10;

  steps.forEach((st, i) => {
    const li = laneIndex(st.lane);
    const x = left + li * laneW + 6;
    const y = stepTop + i * (cardH + gap);
    const l = lanes[li];

    // Card
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...l.color);
    doc.setLineWidth(1);
    doc.roundedRect(x, y, cardW, cardH, 4, 4, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND.dark);
    doc.setFontSize(8);
    const titleLines = doc.splitTextToSize(s(st.title), cardW - 8);
    doc.text(titleLines, x + 4, y + 11);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.muted);
    doc.setFontSize(7);
    const noteLines = doc.splitTextToSize(s(st.note), cardW - 8);
    doc.text(noteLines, x + 4, y + 26);

    // Branch pill
    if (st.branch) {
      const bColor = st.branch === 'A' ? [5, 150, 105] : [194, 65, 12];
      doc.setFillColor(...bColor);
      doc.roundedRect(x + cardW - 14, y + 3, 11, 8, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(6);
      doc.text(s(st.branch), x + cardW - 8.5, y + 9, { align: 'center' });
    }

    // Status pill in status lane
    const sx = left + 4 * laneW + 6;
    const sw = cardW;
    doc.setFillColor(...st.statusColor);
    doc.setDrawColor(...st.statusColor);
    doc.setLineWidth(0.8);
    doc.roundedRect(sx, y + 8, sw, cardH - 16, (cardH - 16) / 2, (cardH - 16) / 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text(s(st.status), sx + sw / 2, y + cardH / 2 + 2, { align: 'center' });

    // Connector arrow from previous step's card to this one
    if (i > 0) {
      const prev = steps[i - 1];
      const prevLI = laneIndex(prev.lane);
      const prevX = left + prevLI * laneW + 6 + cardW / 2;
      const prevY = stepTop + (i - 1) * (cardH + gap) + cardH;
      const curX = x + cardW / 2;
      const curY = y;
      doc.setDrawColor(148, 163, 184);
      doc.setLineWidth(0.6);
      // Bezier via midpoint
      const midY = (prevY + curY) / 2;
      doc.lines([[0, midY - prevY], [curX - prevX, 0], [0, curY - midY]], prevX, prevY);
      // Arrowhead
      doc.setFillColor(148, 163, 184);
      doc.triangle(curX - 2.5, curY - 4, curX + 2.5, curY - 4, curX, curY, 'F');
    }
  });

  // Footer legend
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  const legendY = laneTop + laneH + 12;
  doc.text(
    s('Tip: Only Engineer / Manager / Admin roles can move a complaint to CLOSED. Technicians can go up to RESOLVED. Returnable items appear on the "Pending Returns" report until the storekeeper marks them returned.'),
    left, legendY, { maxWidth: usable }
  );
}

// Pre-loaded logo data URL helper
async function loadLogoDataUrl() {
  try {
    const res = await fetch('/branding/sgk-apps-logo.jpg');
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateTrainingManualPDF({ companyName = 'Your Company', filename = 'SGK_Apps_Training_Manual.pdf', view = false } = {}) {
  const logoDataUrl = await loadLogoDataUrl();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const headerOpts = { logoDataUrl, companyName };

  // === COVER ===
  drawCover(doc, headerOpts);

  // === TOC ===
  doc.addPage();
  drawHeader(doc, headerOpts);
  let y = 70;
  y = h1(doc, 'Table of Contents', y);
  const toc = [
    '1.  Introduction & Welcome',
    '2.  Getting Started: Sign-up, Onboarding, First Login',
    '3.  Subscription Plans & What You Get',
    '4.  Roles & Permissions (all 13 roles)',
    '5.  Menu-by-Menu Walkthrough (all modules)',
    '6.  Common Workflows (Complaint -> Work Order -> Bill)',
    '7.  Admin Playbook: Inviting Users & Setting Permissions',
    '8.  Reports, PDFs & Branding',
    '9.  Tips for Field Teams (Mobile + QR scanner)',
    '10. FAQ & Troubleshooting',
    '11. Support & Resources',
  ];
  y = bullets(doc, toc, y, headerOpts);

  // === 1. INTRODUCTION ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '1. Introduction & Welcome', y);
  y = paragraph(doc,
    'SGK Apps FM Flexi Pro is a mobile-first, AI-enabled, multi-tenant Facilities Management platform built for UAE property managers, real-estate operators, and FM firms. This manual walks every role through the app in plain language - from the first sign-up to running daily operations as a profit center.',
    y, headerOpts);
  y = h2(doc, 'Who is this manual for?', y);
  y = bullets(doc, [
    'Company Admins setting up a new tenant and inviting team members',
    'Trainers onboarding cleaning, security, technician, and accounting staff',
    'End users (managers, accountants, technicians) who want to learn one feature quickly',
    'Auditors who need to verify what each role can and cannot see',
  ], y, headerOpts);
  y = h2(doc, 'How the app is organised', y);
  y = paragraph(doc, 'Every screen lives under one of 7 colour-coded sidebar groups: Overview, Operations, People, Finance, Compliance, Insights, Administration. Your role decides which groups appear. SuperAdmin sees everything across all tenants; specialised roles (Storekeeper, HR Manager, Security Officer, Procurement Officer) land on a dedicated dashboard tuned to their day-to-day tasks.', y, headerOpts);

  // === 2. GETTING STARTED ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '2. Getting Started', y);
  y = h2(doc, 'Sign-up options', y);
  y = bullets(doc, [
    'Demo (read-only): tap "Try Demo" on the landing page. You see sample data, cannot make changes. A reminder pops up inviting you to start a free 7-day trial for full features.',
    'Trial (7 days free): "Continue with Google" or "Sign in with Email" creates your own isolated workspace. You become FULL_ACCESS for your company.',
    'Direct paid subscription: pick a plan on the landing page and pay via Stripe. Your company is immediately marked active and the trial gate is removed.',
  ], y, headerOpts);
  y = h2(doc, 'Onboarding wizard (required for every new tenant)', y);
  y = paragraph(doc, 'On first login you are redirected to /onboarding. The wizard captures the data we need to brand your reports and contact you. Required fields:', y, headerOpts);
  y = bullets(doc, [
    'Company name (or your personal name if individual)',
    'Contact person',
    'Phone (+971 ...)',
    'Email',
    'Location - city or emirate (e.g. Dubai)',
    'Industry - Real Estate, Facilities, Hospitality, Retail, etc.',
    'Optional: logo upload (PNG/JPG, < 500KB), full address, currency, Arabic UI toggle',
  ], y, headerOpts);
  y = paragraph(doc, 'Until these are filled the backend returns HTTP 403 ONBOARDING_REQUIRED on every other endpoint - protects your data from incomplete profiles.', y, headerOpts);

  // === 3. SUBSCRIPTION PLANS ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '3. Subscription Plans', y);
  y = paragraph(doc, 'Plans are tiered. Higher plans unlock more feature flags. SuperAdmin can change a tenant\'s plan at any time, and admins can toggle individual features within their plan.', y, headerOpts);
  autoTable(doc, {
    startY: y,
    head: [[s('Plan'), s('Monthly'), s('Includes')]],
    body: [
      ['Starter', 'Free / $0', 'Core: Dashboard, Buildings, Assets, Complaints, PPM, Staff, Custom Reports'],
      ['Standard', '$29', 'Starter + AMC Contracts, Tenant Portal, basic Accounting'],
      ['Pro', '$79', 'Standard + HR, AI Insights, Quotations, Vouchers, Advanced Analytics'],
      ['Enterprise', '$199', 'Pro + Purchases, LPO, Procurement, Advanced Accounting, all premium modules'],
    ].map(r => r.map(s)),
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: BRAND.primary, textColor: 255 },
    alternateRowStyles: { fillColor: [255, 248, 240] },
    margin: { left: 32, right: 32 },
  });
  y = doc.lastAutoTable.finalY + 14;
  y = ensureSpace(doc, y, 60, headerOpts);
  y = h2(doc, 'Trial gate', y);
  y = paragraph(doc, 'Every new trial company gets 7 days. Your trial banner turns amber during the trial and red after expiry. To keep access you either pay via Stripe (instant) or wait for SuperAdmin manual approval.', y, headerOpts);

  // === 4. ROLES & PERMISSIONS ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '4. Roles & Permissions', y);
  y = paragraph(doc, 'FM Flexi Pro ships with 13 system roles. The first user of every company is FULL_ACCESS. SuperAdmin is for SGK Apps platform staff only.', y, headerOpts);
  autoTable(doc, {
    startY: y,
    head: [[s('Role'), s('Best for'), s('Key abilities')]],
    body: [
      ['SUPERADMIN', 'SGK Apps platform staff', 'Sees ALL tenants, approves trials, changes plans, manages subscriptions'],
      ['FULL_ACCESS', 'Company "user admin"', 'Invite/block users, reset passwords, toggle features, full company access'],
      ['ADMIN', 'Company admin', 'Same as FULL_ACCESS minus billing changes'],
      ['CEO', 'Top executive', 'Read-write on operations + Executive + Finance dashboards'],
      ['MANAGER', 'Department lead', 'Operations + reports; can dispatch work orders, approve service reports'],
      ['ACCOUNTANT', 'Finance team', 'Invoices, bills, payments, aging, P&L, AR/AP. Locked out of HR/payroll'],
      ['PROCUREMENT_OFFICER', 'Buyer / purchasing', 'RFQ -> Quotation -> LPO -> GRN. Suppliers, Supplier dashboard, Sales/Purchase PnL'],
      ['HR_MANAGER', 'HR team', 'Staff, Attendance, Leaves, Payroll. Privacy-isolated from finance and ops'],
      ['STOREKEEPER', 'Warehouse / stores', 'Stock, GRN, material issuance, receives material-request notifications'],
      ['SECURITY_OFFICER', 'Security supervisor', 'Patrol schedules, incidents, gate-pass, site inspections, QR scan checkpoints'],
      ['CS_SUPERVISOR', 'Customer service', 'Front-line complaints triage and tenant communications'],
      ['TECHNICIAN', 'Field engineer', 'Work orders, scan asset QR, capture labour + materials, complete service reports'],
      ['TENANT', 'Building tenant', 'Raise complaints, view own invoices, see notices'],
    ].map(r => r.map(s)),
    styles: { fontSize: 8, cellPadding: 4, valign: 'top' },
    headStyles: { fillColor: BRAND.primary, textColor: 255 },
    alternateRowStyles: { fillColor: [255, 248, 240] },
    columnStyles: { 0: { cellWidth: 100, fontStyle: 'bold' }, 1: { cellWidth: 130 } },
    margin: { left: 32, right: 32 },
  });

  // === 5. MENU-BY-MENU ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '5. Menu-by-Menu Walkthrough', y);
  const modules = [
    ['Dashboard', 'KPI overview with drill-down charts. Specialist roles see a focused workbench instead.'],
    ['Buildings', 'Property master with photos, documents, owner info, license, insurance, QR scan.'],
    ['Assets', 'Equipment register; auto-generates a QR sticker for every asset (printable).'],
    ['Work Orders', 'Single execution record. Dispatch from complaint/PPM, auto-creates service report on completion, can bill the tenant.'],
    ['Inspections & Snagging', 'Pre/post-handover, DLP, fitout-exit room-by-room snag list with photos before/after; one-click convert snag to WO.'],
    ['Complaints', 'Auto ticket TKT-YYMM-BLDG-NNNN. Threaded messages, attachments, dispatch button.'],
    ['PPM Tasks', 'Planned preventive maintenance with overdue highlighting and AMC linkage.'],
    ['Schedules', 'Cleaning / security / technician daily, weekly, monthly schedules with auto-renew.'],
    ['Housekeeping', 'Area-by-area cleaning checklists, recurring frequency, daily task tracking + auto-generation.'],
    ['SOPs & SLAs', 'Document library for procedures, SLAs, emergency protocols.'],
    ['Service Reports', 'In-house and service-provider reports with material usage and SLA tracking.'],
    ['Utilities', 'Per-building utility readings, charts, AI spike remarks, meter reference.'],
    ['PPM Calendar', 'Month grid showing in-house vs service-provider PPM tasks.'],
    ['Staff / Attendance / HR', 'People ops: profile, attendance with overtime, leave approval, payroll with payslip PDF.'],
    ['Accounting / Advanced Accounting', 'Invoices, Bills, Payments, Aging, P&L, Trial Balance, Bank & Cash accounts, COA, Advances.'],
    ['Quotations / Vouchers / LPO', 'Sales/purchase quotes -> approval stamp -> convert to invoice or LPO. Payment + Receipt vouchers.'],
    ['Procurement (Delivery & Payments)', 'GRN with PO sync, Payment Requests pipeline, auto-creates payment voucher.'],
    ['Stock / Stock Audit', 'Inventory with low-stock alerts, manual movements, audit log with PDF export.'],
    ['Suppliers / Supplier Dashboard', 'Vendor register with KPI scorecard, statement of account per supplier per building.'],
    ['AMC Contracts / Provider KPI', 'Annual maintenance contracts with scope items, compliance %, A-F grading.'],
    ['Certificates / Document Vault', 'Statutory cert tracker with expiry; vault for licenses, warranties, insurance, staff docs.'],
    ['Executive / Finance Dashboards', 'C-suite views: KPIs, cost per property, cost trend, AR/AP aging, cashflow, P&L.'],
    ['Custom Reports / Workflows / AI Insights', 'Custom report builder, visual workflow maps, Claude Sonnet powered AI insights.'],
    ['Notes & Reminders / My Tasks', 'Personal sticky notes + the unified My Tasks inbox aggregating all your pending work.'],
    ['Notifications / Audit Log / Backups / Role Access', 'In-app alerts, immutable audit trail, JSON backups, per-role menu permission editor.'],
    ['Subscription / Settings / Help', 'Plan management with Stripe checkout, company settings, this manual.'],
  ];
  modules.forEach(([name, desc]) => {
    y = ensureSpace(doc, y, 32, headerOpts);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.primary);
    doc.text(s('> ' + name), 32, y);
    y += 12;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.body);
    const lines = doc.splitTextToSize(s(desc), doc.internal.pageSize.getWidth() - 64);
    lines.forEach(l => { doc.text(l, 44, y); y += 12; });
    y += 4;
  });

  // === 6. COMMON WORKFLOWS ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '6. Common Workflows', y);
  y = h2(doc, 'A. Complaint -> Service Report -> Bill Tenant', y);
  y = bullets(doc, [
    'Tenant raises a complaint via portal or staff logs it. Auto ticket # generated.',
    'CS_Supervisor triages, assigns to a technician or service provider.',
    'Click "Dispatch Work Order" - WO is auto-created with WO/INT/BLDG/NNNN/MM/YY format.',
    'Technician opens WO on mobile, taps "Start", captures labour + issues material from stock.',
    'On "Complete", a Service Report is auto-generated; complaint flips to resolved.',
    'If material cost > 0 and tenant is billable, click "Bill Tenant" - draft invoice appears in Accounting.',
    'Accountant reviews, sends invoice, records payment when received.',
  ], y, headerOpts);

  // === ILLUSTRATED SWIMLANE DIAGRAM ===
  drawWorkflowDiagram(doc, headerOpts);
  y = h2(doc, 'B. Purchase Cycle (Procurement Officer + Storekeeper)', y);
  y = bullets(doc, [
    'Create Quotation (purchase) for items needed.',
    'Approve quotation -> convert to LPO (Purchase Order).',
    'Supplier delivers; Storekeeper raises GRN (Delivery Note) referencing the LPO.',
    'Bill arrives -> Procurement Officer matches it to the LPO (over/under variance flagged).',
    'Accountant approves Payment Request -> auto-creates Payment Voucher with cheque/bank info.',
  ], y, headerOpts);
  y = h2(doc, 'C. Inspection -> Snag -> Work Order', y);
  y = bullets(doc, [
    'Create inspection (Pre-handover, DLP, Periodic, etc.) for a building.',
    'Walk the site, add snags with severity (minor/major/critical) + before photos.',
    'For each open snag click "Convert to WO" - mints WO and copies photos.',
    'Contractor rectifies; upload after photo, mark rectified.',
    'Re-inspect, sign off, generate branded snag report PDF.',
  ], y, headerOpts);

  // === 7. ADMIN PLAYBOOK ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '7. Admin Playbook', y);
  y = h2(doc, 'Invite a new user', y);
  y = bullets(doc, [
    'User Management -> Create User',
    'Enter email + name, pick role from dropdown (all 13 roles available)',
    'System generates a 12-character one-time password (shown ONCE - copy it)',
    'Share email + temp password with the new user',
    'On first login they are forced to change password',
  ], y, headerOpts);
  y = h2(doc, 'Customise role permissions', y);
  y = bullets(doc, [
    'Role Access page -> pick a role',
    'Tick/untick menu paths the role can see',
    'Save. Backend still enforces capability checks (read/write rules cannot be bypassed via UI)',
  ], y, headerOpts);
  y = h2(doc, 'Block, unblock, reset password', y);
  y = bullets(doc, [
    'User row -> lock icon to block (active sessions revoked instantly)',
    'Unlock icon to restore',
    'Key icon issues a new temp password (shown ONCE)',
  ], y, headerOpts);

  // === 8. REPORTS & PDFS ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '8. Reports, PDFs & Branding', y);
  y = bullets(doc, [
    'Every PDF (invoice, payslip, asset register, stock audit, inspection report, this manual) carries your uploaded logo + company name + address as a letterhead.',
    'Footer always reads "SGK Apps (TM), Branding and a Product of VRV Technical & Technology Solutions FZ LLC".',
    'Custom Reports: pick data source, choose columns, filter, save as template (browser-local), Download or Print.',
    'Backups: admin-only JSON snapshot of every tenant collection; automatic 24h, manual on-demand, restorable.',
  ], y, headerOpts);

  // === 9. MOBILE / QR ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '9. Tips for Field Teams', y);
  y = bullets(doc, [
    'Open the app on any phone browser - bottom navigation appears with 4 main tabs + More sheet.',
    'Tap the Scan button (top bar). Allow camera. Point at any asset QR sticker - opens that asset.',
    'Or scan a Building QR (printable from /buildings) and choose "Create Work Order" to pre-fill.',
    'Inside a Work Order use Quick Capture: Add Labour and Issue Material directly from the WO view dialog.',
    'Tenant signature: ask tenant to sign on screen at completion; signature is embedded in the service report.',
    'Photos: every snag, work order, and inspection lets you attach before/after photos.',
  ], y, headerOpts);

  // === 10. FAQ ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '10. FAQ & Troubleshooting', y);
  const faqs = [
    ['I logged in with Google but cannot access modules.', 'You are still in the onboarding wizard. Complete the required company profile fields (name, phone, email, location, industry) and click Save.'],
    ['My trial says expired but I just signed up.', 'Check the trial banner. If you have not crossed 7 days the gate is not blocking you - sign out and back in. If days exceeded, click Subscribe or wait for SuperAdmin approval.'],
    ['The demo account is read-only - how do I really test?', 'Click the "Start 7-day Free Trial" CTA on the demo welcome popup or on the landing page. Google sign-in is enough.'],
    ['I cannot see HR or Accounting in the sidebar.', 'Either your plan does not include the feature, or your role is gated. Ask your Admin to upgrade the plan or change your role.'],
    ['How do I make my company logo show on PDFs?', 'Settings -> upload logo. It auto-appears on every PDF the app generates.'],
    ['Can my team use the app in Arabic?', 'Yes. Settings -> toggle "Enable Arabic". Every user in your company gets a language switcher in the top bar.'],
    ['What happens if I delete a user?', 'Their record stays for audit purposes but their active sessions are revoked. To re-enable, restore via SuperAdmin support.'],
    ['Does the backup contain all my data?', 'Yes - every tenant-owned collection is included (users, buildings, assets, complaints, invoices, vault, work orders, housekeeping, inspections, etc.).'],
    ['What is the difference between Schedules and PPM Tasks?', 'PPM Tasks are individual maintenance items linked to AMC contracts. Schedules are recurring patterns (e.g. "Clean lobby daily"). Housekeeping module manages cleaning specifically with checklists.'],
    ['How do I escalate to SGK Apps support?', 'See section 11 below.'],
  ];
  faqs.forEach(([q, a]) => {
    y = ensureSpace(doc, y, 40, headerOpts);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.navy);
    const qLines = doc.splitTextToSize(s('Q: ' + q), doc.internal.pageSize.getWidth() - 64);
    qLines.forEach(l => { doc.text(l, 32, y); y += 12; });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.body);
    const aLines = doc.splitTextToSize(s('A: ' + a), doc.internal.pageSize.getWidth() - 64);
    aLines.forEach(l => { doc.text(l, 32, y); y += 12; });
    y += 4;
  });

  // === 11. SUPPORT ===
  doc.addPage(); drawHeader(doc, headerOpts); y = 70;
  y = h1(doc, '11. Support & Resources', y);
  y = bullets(doc, [
    'In-app Help: top sidebar -> Help & Manuals',
    'SuperAdmin contact: emiserve20@gmail.com',
    'Sales: sales@fmflexipro.app',
    'Technical: support@sgkapps.com',
    'Product Owner: VRV Technical & Technology Solutions FZ LLC',
  ], y, headerOpts);
  y = paragraph(doc, 'For feature requests, training bookings, and bulk licensing reach out via the addresses above. We aim to respond within 1 business day.', y, headerOpts);

  drawFooter(doc);

  if (view) {
    const url = URL.createObjectURL(doc.output('blob'));
    window.open(url, '_blank');
  } else {
    doc.save(filename);
  }
}
